import { t } from 'i18next';

const getHeartRateZones = (maxHeartRate) => [
    {
        label: t('exercise.zones.zone0'),
        min: 0,
        max: 0.5 * maxHeartRate,
        count: 0,
        value: 0,
        color: '#aae692'
    },
    {
        label: t('exercise.zones.zone1'),
        min: 0.5 * maxHeartRate,
        max: 0.6 * maxHeartRate,
        count: 0,
        value: 0,
        color: '#2c9e00'
    },
    {
        label: t('exercise.zones.zone2'),
        min: 0.6 * maxHeartRate,
        max: 0.7 * maxHeartRate,
        count: 0,
        value: 0,
        color: '#f8eeb8'
    },
    {
        label: t('exercise.zones.zone3'),
        min: 0.7 * maxHeartRate,
        max: 0.8 * maxHeartRate,
        count: 0,
        value: 0,
        color: '#e6c72e'
    },
    {
        label: t('exercise.zones.zone4'),
        min: 0.8 * maxHeartRate,
        max: 0.9 * maxHeartRate,
        count: 0,
        value: 0,
        color: '#f3a7a1'
    },
    {
        label: t('exercise.zones.zone5'),
        min: 0.9 * maxHeartRate,
        max: 255,
        count: 0,
        value: 0,
        color: '#f44336'
    }
];

/**
 * Calculate the percentage of time spent in each heart rate zone.
 *
 * @param {number[]} heartRates - Array of heart rates.
 * @param {number} maxHeartRate - Maximum heart rate (MHR).
 * @param {string} ti - Start time in ISO format.
 * @param {string} tf - End time in ISO format.
 * @returns {Object} An object containing the percentage of time spent in each heart rate zone.
 */
export const calculateHeartRateZones = (heartRates, user_data, ti, tf) => {
    const classicZones = getHeartRateZones(user_data.maxHeartRate);
    const total = heartRates.length;

    heartRates.forEach((hr) => {
        classicZones.forEach((zone) => {
            if (hr >= zone.min && hr < zone.max) zone.count++;
        });
    });

    classicZones.forEach((zone) => {
        zone.value = Math.round((zone.count / total) * 100);
        zone.time = calculateTimeInZone(zone.value, new Date(ti), new Date(tf));
    });

    const summaryZones = [
        {
            label: t('exercise.zones.lightZone'),
            min: 0,
            max: user_data.minTrainingHeartRate,
            count: 0,
            value: 0,
            color: '#2c9e00'
        },
        {
            label: t('exercise.zones.training'),
            min: user_data.minTrainingHeartRate,
            max: user_data.trainingHeartRate,
            count: 0,
            value: 0,
            color: '#e6c72e'
        },
        {
            label: t('exercise.zones.alert'),
            min: user_data.trainingHeartRate,
            max: user_data.maxHeartRate,
            count: 0,
            value: 0,
            color: '#f44336'
        }
    ];

    heartRates.forEach((hr) => {
        summaryZones.forEach((zone) => {
            if (hr >= zone.min && hr < zone.max) zone.count++;
        });
    });

    summaryZones.forEach((zone) => {
        zone.value = Math.round((zone.count / total) * 100);
        zone.time = calculateTimeInZone(zone.value, new Date(ti), new Date(tf));
    });

    const groupedZones = [
        {
            label: t('exercise.zones.light'),
            value: Math.round(classicZones[0].value + classicZones[1].value),
            time: calculateTimeInZone(
                classicZones[0].value + classicZones[1].value,
                new Date(ti),
                new Date(tf)
            ),
            color: '#2c9e00'
        },
        {
            label: t('exercise.zones.moderate'),
            value: Math.round(classicZones[2].value + classicZones[3].value),
            time: calculateTimeInZone(
                classicZones[2].value + classicZones[3].value,
                new Date(ti),
                new Date(tf)
            ),
            color: '#e6c72e'
        },
        {
            label: t('exercise.zones.intense'),
            value: Math.round(classicZones[4].value + classicZones[5].value),
            time: calculateTimeInZone(
                classicZones[4].value + classicZones[5].value,
                new Date(ti),
                new Date(tf)
            ),
            color: '#f44336'
        }
    ];

    return { classicZones, groupedZones, summaryZones };
};

const calculateTimeInZone = (percentage, startTime, endTime) => {
    const totalDuration = (endTime - startTime) / 1000;
    const timeInZone = totalDuration * (percentage / 100);

    const minutes = Math.floor(timeInZone / 60);
    const seconds = Math.floor(timeInZone % 60);

    return `${minutes}min ${seconds}s`;
};
